import { Link } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import { whatsappLink } from "@/config/company";
import { motion } from "framer-motion";
import {
  Truck, Globe2, Plane, Ship, Package, Warehouse, Settings,
  ArrowRight, MessageCircle,
} from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";


const services = [
  {
    icon: Truck,
    title: "Domestic courier",
    desc: "Doorstep pickup and delivery across South India, running on our own network — not handed off to a third party the moment it leaves our hands.",
    points: ["24–48 hour delivery windows", "Real-time tracking", "Own network across South India"],
  },
  {
    icon: Globe2,
    title: "International courier",
    desc: "Parcels and documents to destinations outside India, handled end to end through vetted partners.",
    points: ["Door-to-door handling", "Customs paperwork support", "Tracked from pickup to arrival"],
  },
  {
    icon: Plane,
    title: "Air cargo",
    desc: "When the deadline can't move, the freight moves by air. For time-critical shipments that standard surface transit can't meet.",
    points: ["Priority routing", "For genuinely urgent freight", "Real-time status updates"],
  },
  {
    icon: Ship,
    title: "Sea cargo",
    desc: "Cost-efficient freight by sea for larger, less time-sensitive consignments.",
    points: ["Lower cost per kg for bulk", "Suited to planned, non-urgent moves"],
  },
  {
    icon: Package,
    title: "Full truck & part load (FTL / PTL)",
    desc: "Freight sized to what you're actually shipping — a dedicated truck for full loads, or a shared one for smaller consignments.",
    points: ["FTL for dedicated, full-load freight", "PTL for smaller consignments", "B2B express delivery within 48 hours"],
  },
  {
    icon: Warehouse,
    title: "Warehousing & fulfillment",
    desc: "Storage and order fulfillment support for businesses that need a physical hub in South India without building their own.",
    points: ["Storage space in Visakhapatnam", "Order pick-and-pack support"],
  },
  {
    icon: Settings,
    title: "Customized logistics solutions",
    desc: "Not every shipment fits a standard category. We design around your actual urgency, volume, and budget — elastic and tailor-made.",
    points: ["Built around your specific shipment", "Flexible on volume and timing"],
  },
];

export default function Services() {
  return (
    <PublicLayout>
      <Seo title={"Courier, Cargo, Freight & Packers Services"} description={"Express courier, part & full truck load, packers and movers, international, hyperlocal and car/bike transport across South India and India. See all Sendtoo services."} path={"/services"} />
      <div className="min-h-screen">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0A183D] to-[#1B3FA0] py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">A 360° logistics company</h1>
            <p className="text-blue-200 mt-4 text-lg">End-to-end solutions for individuals, businesses, and industries.</p>
          </div>
        </div>

        {/* Services */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 2) * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg p-7"
                >
                  <s.icon className="w-7 h-7 text-brand-500 mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4">{s.desc}</p>
                  <ul className="space-y-1.5">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-xs text-slate-600">
                        <div className="w-1 h-1 rounded-full bg-brand-500 mt-1.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-brand-500">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">Not sure which service fits?</h2>
            <p className="text-blue-100 mb-8">Tell us what you're shipping — we'll size the right solution to it.</p>
            <div className="flex justify-center gap-3 flex-wrap">
              <Link
                to="/book"
                data-testid="services-book-btn"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white text-brand-500 font-semibold rounded-sm hover:bg-blue-50 transition-colors"
              >
                Book a shipment <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={whatsappLink("Hi Sendtoo, I need a quote")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-whatsapp-btn"
                className="inline-flex items-center gap-2 px-7 py-3 bg-[#25D366] text-white font-semibold rounded-sm hover:bg-[#1FBE5A] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
              </a>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
